import axiosIns from '@axios'
import { Provider } from '@/models/provider'
import { ProvidersSlugEnum } from '@/common/enums/providersEnum'
import { useRootStore } from '@/stores/root'

export interface IBackupPayload {
  fileSysId?: number
  dbSysId?: number
  backupId?: number
  restore_on_site_id?: number
}

export interface IDataSyncPayload {
  to_site_id: number
  themeFiles?: boolean
  pluginFiles?: boolean
  mediaUploads?: boolean
  wpContentFiles?: boolean
  webRootFiles?: boolean
  excludeRobotsTxt?: boolean
  skipHttpStatusCheck?: boolean
  skipMaintenanceMode?: boolean
  entireDatabase?: boolean
  preserveJetpackLicenses?: boolean
}

export const getAllSites = async (payload: {
  limit: number
  skip: number
  search: string
  type: string
}) => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      payload.search = payload.search
        ?.replace('http://', '')
        .replace('https://', '')
      const url = '/site'
      const { data } = await axiosIns.get(url, {
        params: {
          search: payload.search,
          per_page: payload.limit ?? 10,
          page: payload.skip,
          type: payload.type || 'all',
        },
      })
      resolve(data?.data)
    } catch (e) {
      reject(e)
    }
  })
}
export const getSharedSites = async ({
  search,
  page,
  perPage,
}: {
  search?: string
  page?: number
  perPage?: number
}) => {
  const url = `/site-access?page=${page ?? 1}&per_page=${perPage ?? 10}${
    search ? `&search=${encodeURIComponent(search)}` : ''
  }`
  const { data } = await axiosIns.get(url)
  return data?.data
}
export const getGeneralSiteDetail = async (siteId: number) => {
  const info = await axiosIns.get(`/site/${siteId}`)
  return info?.data?.data
}
export const markSiteForDeletion = async ({
  siteId,
  reason,
}: {
  siteId: number
  reason?: string
}) => {
  return axiosIns.put(`/site/${siteId}/remove-on-renewal`, {
    reason,
  })
}
export const deleteStagingSite = async ({ siteId }: { siteId: number }) => {
  return axiosIns.delete(`/site/${siteId}/staging`)
}
export const unmarkSiteForDeletion = async (siteId: number) => {
  return axiosIns.put(`/site/${siteId}/keep-on-renewal`)
}

export const getSiteInfo = async (provider: number, siteId: number) => {
  const info = await axiosIns.get(`/site/${siteId}/site-information`)
  return info?.data?.data
}

export const getOneClickWpAdminLoginUrl = async (siteId: number) => {
  const rootStore = useRootStore()
  // rootStore.showSnackbar(
  //   'Generating one-click login link. This may take a while. Please wait',
  //   'info',
  //   5000,
  // )
  const { data } = await axiosIns.get(`/site/${siteId}/one-click-login`)
  return data?.data
}
export const downloadBackups = async (siteId: number, provider: Provider) => {
  return await axiosIns.get(
    `/site/${siteId}/site-backup`,
    provider?.slug === ProvidersSlugEnum.PRESSABLE
      ? {
          responseType: 'blob',
        }
      : {},
  )
}

export const downloadSiteBackupAgainstIds = async (
  siteId: number,
  data?: IBackupPayload,
) => {
  return await axiosIns.post(
    `/site/${siteId}/current-backup`,
    data?.backupId
      ? {
          backup_id: data?.backupId,
        }
      : {
          filesystem_id: data?.fileSysId,
          database_id: data?.dbSysId,
        },
  )
}

export const getSiteSftpCreds = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/credentials`)
  return data?.data
}

export const regenerateSiteSftpCreds = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/credentials/reset`)
  return data?.data
}
export const getDbLoginUrl = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/db-login`)
  return data?.data
}

export const clearSiteCache = async (siteId: number) => {
  return axiosIns.get(`/site/${siteId}/cache-clean`)
}

export const getSiteBackups = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/backups`)
  return data?.data
}

export const restoreSiteBackup = (siteId: number, data?: IBackupPayload) => {
  return axiosIns.post(`/site/${siteId}/backup/restore`, {
    filesystem_id: data?.fileSysId,
    database_id: data?.dbSysId,
    backup_id: data?.backupId,
    restore_on_site_id: data?.restore_on_site_id,
  })
}

export const syncSites = (siteId: number, data: IDataSyncPayload) => {
  return axiosIns.post(`/site/${siteId}/data-sync`, {
    to_site_id: data.to_site_id,
    themeFiles: data.themeFiles,
    pluginFiles: data.pluginFiles,
    mediaUploads: data.mediaUploads,
    wpContentFiles: data.wpContentFiles,
    webRootFiles: data.webRootFiles,
    excludeRobotsTxt: data.excludeRobotsTxt,
    skipHttpStatusCheck: data.skipHttpStatusCheck,
    skipMaintenanceMode: data.skipMaintenanceMode,
    entireDatabase: data.entireDatabase,
    preserveJetpackLicenses: data.preserveJetpackLicenses,
  })
}

export const addNewDomain = async (siteId: number, domain: string) => {
  const { data } = await axiosIns.post(`/site/${siteId}/add-domain`, {
    name: domain,
  })
  return data?.data
}

export const updatePrimaryDomain = async (siteId: number, domainId: number) => {
  const { data } = await axiosIns.get(
    `/site/${siteId}/primary-domain/${domainId}`,
  )
  return data?.data
}

export const deleteDomainByID = async (siteId: number, domainId: number) => {
  const { data } = await axiosIns.delete(
    `/site/${siteId}/remove-domain/${domainId}`,
  )
  return data?.data
}

export const reapplySslToPrimaryDomain = async (siteId: number) => {
  const { data } = await axiosIns.put(
    `/site/${siteId}/domain/primary/reapply-ssl`,
  )
  return data?.data
}

export const getSiteWpThemes = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/wp-theme`)
  return data?.data
}
export const getSiteWpCoreInfo = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/wp-core`)
  return data?.data
}
export const getSiteWpPlugins = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/wp-plugin`)
  return data?.data
}

export const checkForSiteWpCoreUpdates = async (siteId: number) => {
  const { data } = await axiosIns.get(
    `/site/${siteId}/wp-core/update/available`,
  )
  return data?.data
}

export const updateSiteWpCore = async (siteId: number, coreVersion: string) => {
  const { data } = await axiosIns.get(
    `/site/${siteId}/wp-core/update/${coreVersion}`,
  )
  return data?.data
}

export const updateAllSiteWpThemes = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/wp-theme/update/all`)
  return data?.data
}

export const updateSiteWpThemeByName = async (
  siteId: number,
  themeName: string,
) => {
  const { data } = await axiosIns.get(
    `/site/${siteId}/wp-theme/update/${themeName}`,
  )
  return data?.data
}

export const updateAllSiteWpPlugins = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/wp-plugin/update/all`)
  return data?.data
}

export const updateSiteWpPluginByName = async (
  siteId: number,
  pluginName: string,
) => {
  const { data } = await axiosIns.get(
    `/site/${siteId}/wp-plugin/update/${pluginName}`,
  )
  return data?.data
}

export const launchStagingSite = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/launch-staging`)
  return data?.data
}

export const cloneSite = async (
  siteId: number,
  cloneSiteData: {
    datacenter: string
    domain: string
    price_id: string
    addons_price_ids?: string[]
  },
) => {
  const { data } = await axiosIns.post(`/site/${siteId}/clone`, {
    ...cloneSiteData,
  })
  return data?.data
}

export const postLiveAutomationSite = async (
  siteId: number,
  automationData: any,
) => {
  const { data } = await axiosIns.post(
    `/site/${siteId}/misc/automate-post-lives`,
    {
      ...automationData,
    },
  )
  return data?.data
}

export const updatePhpVersion = async (siteId: number, phpVersion: string) => {
  const { data } = await axiosIns.put(`/site/${siteId}/update-php`, {
    php_version: phpVersion,
  })
  return data?.data
}
export const deleteSiteInstantly = async (
  siteId: number,
  reason: string,
  followupAnswer?: string,
) => {
  return await axiosIns.patch(`/site/${siteId}/delete`, {
    reason,
    followupAnswer,
  })
}

export const pushToLive = async (siteId: number) => {
  const { data } = await axiosIns.post(`/site/${siteId}/push-to-live/`)
  return data?.data
}

export const getPostMarkDomainDetailsAgainst = async (
  post_mark_domain_id: number,
) => {
  const info = await axiosIns.get(
    `/postmark/domain/look-for-both/${post_mark_domain_id}`,
  )
  return info?.data?.data
}

export const resetPluginKey = async (siteId: number) => {
  const { data } = await axiosIns.patch(`/site/${siteId}/plugin-key`)
  return data?.data
}

export const syncSite = async (siteId: number, providerSiteId: string) => {
  const { data } = await axiosIns.put('/admin-site/sync', {
    provider_site_id: providerSiteId,
    site_id: siteId,
  })
  return data?.data
}

export const getPSISites = async (
  strategy: 'mobile' | 'desktop',
  is_above: boolean,
  metric: 'page_speed_score' | 'load_time',
  page: number,
  perPage: number,
  search: string,
) => {
  const { data } = await axiosIns.post('/site/with-psi-criteria', {
    strategy,
    is_above,
    metric,
    page,
    per_page: perPage,
    search,
  })
  return data?.data
}

export const changeSitePlan = async (siteId: number, planId: string) => {
  const { data } = await axiosIns.put(`/site-plan/change/${siteId}`, {
    price_id: planId,
  })
  return data?.data
}

export const getPlanProratedPrice = async (siteId: number, planId: string) => {
  const { data } = await axiosIns.get(
    `/site-plan/prorated-change/${siteId}/${planId}`,
  )
  return data?.data
}

export const getSitePhpLogs = async (
  siteId: number,
  status?: string,
  scrollId?: string,
) => {
  const { data } = await axiosIns.get(
    `/site/${siteId}/php-logs?status=${status}&scroll_id=${scrollId}`,
  )
  return data?.data
}

export const getSiteServerLogs = async (
  siteId: number,
  status?: string,
  scrollId?: string,
  requestType?: string,
  userIp?: string,
) => {
  const { data } = await axiosIns.get(
    `/site/${siteId}/server-logs?status=${status}&scroll_id=${scrollId}&request_type=${requestType}&user_ip=${userIp}`,
  )
  return data?.data
}

export const exportAllSiteToCSV = async () => {
  const { data } = await axiosIns.get('/site/export-to-csv', {
    timeout: 60 * 1000, // 1 minute
  })
  return data?.data
}

export const createBothFSAndDBBackupsOnDemand = async (siteId: number) => {
  const { data } = await axiosIns.post(`/site/${siteId}/backups/on-demand/all`)
  return data?.data
}

export const createFSBackupOnDemand = async (siteId: number) => {
  const { data } = await axiosIns.post(`/site/${siteId}/backups/on-demand/fs`)
  return data?.data
}

export const createDBBackupOnDemand = async (siteId: number) => {
  const { data } = await axiosIns.post(`/site/${siteId}/backups/on-demand/db`)
  return data?.data
}

export const deleteOnDemandBackup = async (
  siteId: number,
  backupId: string,
) => {
  const { data } = await axiosIns.delete(
    `/site/${siteId}/backups/on-demand/${backupId}`,
  )
  return data?.data
}

export const renewStagingSite = async (siteId: number) => {
  const { data } = await axiosIns.get(`site/staging/${siteId}/renew`)
  return data?.data
}

export const editSiteDisplayName = async (siteId: number, name: string) => {
  const { data } = await axiosIns.put(`site/${siteId}/display-name`, {
    name,
  })
  return data?.data
}

export const getAtomicTXTRecordsOfPrimaryDomain = async (siteId: number) => {
  const { data } = await axiosIns.get(
    `site/${siteId}/domain/primary/atomic-txt-records`,
  )
  return data?.data
}

export const getDnsTransactionalRecords = async (siteId: number) => {
  const { data } = await axiosIns.get(
    `site/${siteId}/dns-transactional-records`,
  )
  return data?.data
}

export const refreshSiteIPs = async (siteId: number) => {
  const { data } = await axiosIns.get(`site/${siteId}/ips/refresh`)
  return data?.data
}

/// domains

export const fetchPendingDKIMDomains = async () => {
  const { data } = await axiosIns.get('/site/domains/pm/pending-dkims')
  return data?.data
}

export const disableDKIMAlerts = async (domainId: number) => {
  const { data } = await axiosIns.put(
    `/site/domains/${domainId}/pm/ignore-dkim-alert`,
  )
  return data?.data
}
export const getPMDomainDetails = async (domainId: number) => {
  const { data } = await axiosIns.get(
    `/site/domains/${domainId}/pm/domain-details`,
  )
  return data?.data
}

// site tags
export const addTagToSite = async (siteId: number, tagName: string) => {
  const { data } = await axiosIns.post(`/site/${siteId}/tags`, {
    name: tagName,
  })
  return data?.data
}

export const removeTagFromSite = async (siteId: number, tagId: number) => {
  await axiosIns.delete(`/site/${siteId}/tags/${tagId}`)
}

export const getSiteTags = async (siteId: number) => {
  const { data } = await axiosIns.get(`/site/${siteId}/tags`)
  return data?.data
}

export const getAllUserTags = async () => {
  const { data } = await axiosIns.get('/site/all-tags')
  return data?.data
}
